package br.com.vvrefeicoes.portal.feature.notifications

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.AppPagination
import br.com.vvrefeicoes.portal.core.ui.components.AppTextField
import br.com.vvrefeicoes.portal.core.ui.components.EmptyState
import br.com.vvrefeicoes.portal.core.ui.theme.AppPalette
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.PeriodMode
import br.com.vvrefeicoes.portal.core.util.formatIsoDateTime
import br.com.vvrefeicoes.portal.domain.model.AppNotification
import br.com.vvrefeicoes.portal.domain.model.NotificationPriority
import br.com.vvrefeicoes.portal.feature.operacional.PeriodSelector
import com.composables.icons.lucide.ChevronDown
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.Search
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.notifications_empty
import portalvv.shared.generated.resources.notifications_priority_high
import portalvv.shared.generated.resources.notifications_priority_low
import portalvv.shared.generated.resources.notifications_priority_medium
import portalvv.shared.generated.resources.notifications_search_placeholder

@Composable
fun NotificationsScreen() {
    val controller = LocalNotifications.current
    LaunchedEffect(controller) { controller.init() }
    NotificationsFeed(controller)
}

@Composable
private fun NotificationsFeed(controller: NotificationsController) {
    Box(modifier = Modifier.fillMaxSize()) {
        if (controller.loading && controller.items.isEmpty() && controller.query.isBlank()) {
            Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                CircularProgressIndicator()
            }
        } else {
            Column(
                modifier =
                    Modifier
                        .fillMaxSize()
                        .verticalScroll(rememberScrollState())
                        .padding(AppSpacing.space16),
                verticalArrangement = Arrangement.spacedBy(AppSpacing.space12),
            ) {
                NotificationsFilters(controller)
                controller.errorMessage?.let {
                    Text(text = it, color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodyMedium)
                }
                if (controller.items.isEmpty()) {
                    EmptyState(message = stringResource(Res.string.notifications_empty))
                } else {
                    controller.items.forEach { notification ->
                        NotificationCard(notification = notification, onOpen = { controller.open(notification.id) })
                    }
                    AppPagination(
                        page = controller.page,
                        pageCount = controller.pageCount,
                        onPageChange = controller::goToPage,
                        modifier = Modifier.align(Alignment.End),
                    )
                }
            }
        }
        if (controller.loading && controller.items.isNotEmpty()) {
            Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.TopEnd) {
                CircularProgressIndicator(modifier = Modifier.padding(AppSpacing.space16).size(20.dp))
            }
        }
    }
}

@Composable
private fun NotificationsFilters(controller: NotificationsController) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(AppSpacing.space16),
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space12),
        ) {
            PeriodSelector(
                selection = controller.period,
                onMode = { mode -> controller.applyPeriod(controller.period.copy(mode = mode)) },
                onMonth = { month -> controller.applyPeriod(controller.period.copy(mode = PeriodMode.MONTH, month = month)) },
                onYear = { year -> controller.applyPeriod(controller.period.copy(mode = PeriodMode.YEAR, year = year)) },
                onCustom = { start, end -> controller.applyPeriod(controller.period.copy(mode = PeriodMode.CUSTOM, start = start, end = end)) },
                bordered = false,
            )
            AppTextField(
                value = controller.query,
                onValueChange = controller::updateQuery,
                placeholder = stringResource(Res.string.notifications_search_placeholder),
                leadingIcon = { Icon(Lucide.Search, contentDescription = null) },
                modifier = Modifier.fillMaxWidth(),
            )
        }
    }
}

@Composable
private fun NotificationCard(
    notification: AppNotification,
    onOpen: () -> Unit,
) {
    var expanded by remember(notification.id) { mutableStateOf(false) }
    var showImage by remember(notification.id) { mutableStateOf(false) }
    val imageUrl = notification.imageUrl?.takeIf { it.isNotBlank() }
    Surface(
        modifier =
            Modifier.fillMaxWidth().clickable {
                if (!expanded) onOpen()
                expanded = !expanded
            },
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(AppSpacing.space16),
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12),
        ) {
            Box(
                modifier =
                    Modifier
                        .padding(top = 6.dp)
                        .size(8.dp)
                        .clip(CircleShape)
                        .background(if (notification.read) Color.Transparent else AppPalette.Primary500),
            )
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(AppSpacing.space4),
            ) {
                notification.publishedAt?.let {
                    Text(
                        text = formatIsoDateTime(it),
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
                Text(
                    text = notification.title,
                    style = MaterialTheme.typography.titleSmall,
                    fontWeight = if (notification.read) FontWeight.Normal else FontWeight.SemiBold,
                )
                if (expanded) {
                    if (imageUrl != null) {
                        AnnouncementImage(
                            source = imageUrl,
                            modifier = Modifier.fillMaxWidth().heightIn(max = 360.dp).clickable { showImage = true },
                        )
                    }
                    if (notification.body.isNotBlank()) {
                        RichBody(html = notification.body, modifier = Modifier.fillMaxWidth())
                    }
                }
            }
            Icon(
                imageVector = Lucide.ChevronDown,
                contentDescription = null,
                modifier = Modifier.graphicsLayer { rotationZ = if (expanded) 180f else 0f },
                tint = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }

    if (showImage && imageUrl != null) {
        AnnouncementImageLightbox(imageUrl = imageUrl, onDismiss = { showImage = false })
    }
}

@Composable
internal fun PriorityChip(priority: NotificationPriority) {
    val (label, color) =
        when (priority) {
            NotificationPriority.LOW -> stringResource(Res.string.notifications_priority_low) to AppPalette.Success500
            NotificationPriority.MEDIUM -> stringResource(Res.string.notifications_priority_medium) to AppPalette.Warning500
            NotificationPriority.HIGH -> stringResource(Res.string.notifications_priority_high) to AppPalette.Error500
        }
    Surface(shape = AppShapes.small, color = color.copy(alpha = 0.14f)) {
        Text(
            text = label,
            style = MaterialTheme.typography.labelSmall,
            color = color,
            modifier = Modifier.padding(horizontal = AppSpacing.space8, vertical = 2.dp),
        )
    }
}
